import { Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { UploadService } from './upload.service';
import { InteractionsService, AdminInteraction } from './interactions.service';
import { CallCenterDatabaseTableData } from '../interfaces/call-center-database.interface';
import { PhoneFormatPipe } from '../shared/pipes';
import { StatusUpdateData } from '../interfaces/call-center-service.interface';

/**
 * CallCenterApiService
 * 
 * Tiszta API és CRUD műveletek kezelése.
 * Felelősség: Adatok lekérdezése, módosítása és backend kommunikáció.
 */
@Injectable({
  providedIn: 'root'
})
export class CallCenterApiService {

  private phoneFormatPipe = new PhoneFormatPipe();

  constructor(
    private uploadService: UploadService,
    private interactionsService: InteractionsService
  ) {}

  /**
   * Cég interakciók lekérdezése log formátumban
   */
  getCompanyLogs(companyId: number): Observable<AdminInteraction[]> {
    return this.interactionsService.getCompanyInteractionsForLogs(companyId);
  }

  /**
   * Cég státusz frissítése kapcsolódó adatokkal
   */
  updateCompanyStatus(companyId: number, statusData: StatusUpdateData): Observable<any> {
    return this.uploadService.updateCompanyStatus(companyId, statusData);
  }

  /**
   * Új vezető hozzáadása céghez
   */
  addCompanyOfficial(companyId: number, officialName: string): Observable<any> {
    const officialData = { fullname: officialName };
    return this.uploadService.addCompanyOfficial(companyId, officialData);
  }

  /**
   * Cég tisztségviselőjének (customer) nevének frissítése
   */
  updateCompanyOfficialName(companyId: number, oldName: string, newName: string): Observable<any> {
    return this.uploadService.updateCompanyOfficialName(companyId, oldName, newName);
  }

  /**
   * Cég telefonszámának vagy email címének szerkesztése
   */
  updateCompanyContactValue(companyId: number, oldValue: string, newValue: string, contactType: 'phone' | 'email'): Observable<any> {
    return this.uploadService.updateContactValue(companyId, {
      old_value: oldValue,
      new_value: newValue,
      contact_type: contactType
    });
  }

  /**
   * Új telefon hozzáadása céghez
   */
  addCompanyPhone(companyId: number, phoneNumber: string): Observable<any> {
    const formattedPhone = this.phoneFormatPipe.transform(phoneNumber);
    const phoneData = { phone: formattedPhone };
    return this.uploadService.addCompanyPhone(companyId, phoneData);
  }

  /**
   * Új email hozzáadása céghez
   */
  addCompanyEmail(companyId: number, email: string): Observable<any> {
    const emailData = { email: email };
    return this.uploadService.addCompanyEmail(companyId, emailData);
  }

  /**
   * Több email hozzáadása céghez
   */
  addMultipleEmails(companyId: number, emails: string[]): Observable<any[]> {
    const emailPromises = emails.map(email => this.addCompanyEmail(companyId, email));
    return forkJoin(emailPromises);
  }

  /**
   * Kapcsolat érvényességi státusz frissítése
   */
  updateContactValidStatus(companyId: number, updateRequest: any): Observable<any> {
    return this.uploadService.updateContactValidStatus(companyId, updateRequest);
  }

  /**
   * Több kapcsolat érvényességi státusz frissítése
   */
  updateMultipleValidStatuses(companyId: number, validStatusUpdates: any): Observable<any[]> {
    const validStatusUpdates$ = Object.entries(validStatusUpdates).map(([contactValue, updateData]: [string, any]) => {
      const { validStatus, contactType } = updateData;
      
      const updateRequest = {
        contact_value: contactValue,
        valid_status: validStatus,
        contact_type: contactType
      };

      return this.updateContactValidStatus(companyId, updateRequest);
    });

    return forkJoin(validStatusUpdates$);
  }

  /**
   * Egyetlen cég adatainak újratöltése backendről
   */
  reloadSingleElement(companyId: number): Observable<CallCenterDatabaseTableData> {
    return this.uploadService.getSingleCompanyData(companyId).pipe(
      map((response: any) => {
        if (response.success && response.data) {
          return {
            ...response.data,
            date: response.data.date ? new Date(response.data.date) : undefined
          };
        }
        throw new Error(`Failed to reload company data for ID: ${companyId}`);
      })
    );
  }
}